import { ChevronDown, ChevronRight, Info, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { StatusBadge } from "../../components/ui/statusBadge";
import { Textarea } from "../../components/ui/textarea";
import { formatDatetime, snakeToTitleCase } from "../../lib/format";
import { getErrorMessage } from "../../lib/query/errors";
import { protocolOf } from "../../lib/url";
import type { StipulationActionReasonView, StipulationActionView, StipulationView } from "./api/types";
import { useApplyStipulationAction } from "./api/useApplyStipulationAction";
import { usePorExceptionMutation } from "./api/usePorExceptionMutation";
import { useStipulationHistory } from "./api/useStipulationHistory";
import StipulationActionMenu from "./StipulationActionMenu";
import { useStipulationEdit, useStipulationEditsStore } from "./stipulationEditsStore";

interface StipulationRowProperties {
  approvalId?: string;
  stip: StipulationView;
}

// currentAction values (lower-cased) that mean the stip is done and has no
// pending action to select, so the dropdown trigger should read "Complete"
// instead of the default "Select One" placeholder.
const RESOLVED_CURRENT_ACTIONS = new Set(["closed", "waived", "exception"]);

// The BFF-sourced `policyTag` is rendered as a link `href`; only allow http(s)
// so a malformed/malicious value (e.g. `javascript:`) can't execute on click.
function isSafePolicyUrl(url: string): boolean {
  const protocol = protocolOf(url);
  return protocol !== undefined && ["http:", "https:"].includes(protocol);
}

// One stipulation card: expand toggle, title, policy link, status badge, action
// menu, optional dealer-instruction note, activity line, and an expandable
// Instructions History. Selections + the note are staged in the edits store so
// the UI updates immediately, then persisted per-row through the BFF action API.
function StipulationRow({ approvalId, stip }: StipulationRowProperties) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [porError, setPorError] = useState<string>();
  const edit = useStipulationEdit(stip.id);
  const clearEdit = useStipulationEditsStore((state) => state.clear);
  const setAction = useStipulationEditsStore((state) => state.setAction);
  const setNote = useStipulationEditsStore((state) => state.setNote);
  const setPorException = useStipulationEditsStore((state) => state.setPorException);
  const applyAction = useApplyStipulationAction(approvalId ?? "");
  const porExceptionMutation = usePorExceptionMutation();

  const { data: fetchedHistory, isLoading: isHistoryLoading } = useStipulationHistory({
    approvalId: approvalId ?? "",
    stipCode: stip.stipCode,
    applicantPk: stip.applicantPk,
    enabled: expanded,
  });

  // Show pre-loaded list data immediately; replace with fresh fetch when available.
  const actionHistory = fetchedHistory ?? stip.history;

  const selectedAction = edit.actionCode
    ? stip.availableActions.find((action) => action.actionCode === edit.actionCode)
    : undefined;
  const selectedReason = selectedAction?.reasons.find((reason) => reason.reasonPk === edit.reasonPk);
  const isResolvedAction = RESOLVED_CURRENT_ACTIONS.has(stip.currentAction?.toLowerCase() ?? "");
  const currentLabel =
    selectedReason?.displayName ?? selectedAction?.displayName ?? (isResolvedAction ? t("stipulations.complete") : undefined);

  const persistAction = (actionCode: string, reasonPk?: number, noteText?: string) => {
    setSaveError(undefined);

    if (!approvalId) {
      return;
    }
    if (stip.stipulationAppliedId === undefined) {
      setSaveError(t("stipulations.saveError", { message: t("stipulations.saveMissingRowId") }));
      return;
    }

    applyAction.mutate(
      {
        stipulationId: stip.id,
        stipCode: stip.stipCode,
        stipulationAppliedId: stip.stipulationAppliedId,
        actionCode,
        reasonPk,
        noteText,
      },
      {
        onSuccess: () => {
          clearEdit(stip.id);
        },
        onError: (error) => {
          setSaveError(t("stipulations.saveError", { message: getErrorMessage(error) }));
        },
      }
    );
  };

  const handleSelect = (action: StipulationActionView, reason?: StipulationActionReasonView) => {
    setAction(stip.id, action.actionCode, reason?.reasonPk);

    if (action.requiresNote) {
      return;
    }

    persistAction(action.actionCode, reason?.reasonPk);
  };

  const handlePorExceptionChange = (checked: boolean) => {
    setPorException(stip.id, checked);
    setPorError(undefined);

    if (!approvalId || !stip.applicantPk) {
      return;
    }

    porExceptionMutation.mutate(
      {
        approvalId,
        applicantPk: stip.applicantPk,
      },
      {
        onError: (error) => {
          setPorError(t("stipulations.saveError", { message: getErrorMessage(error) }));
          setPorException(stip.id, false);
        },
      }
    );
  };

  return (
    <div className="border-border rounded border border-l-2 p-1.5">
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={t("stipulations.toggleDetails")}
            onClick={() => setExpanded((previous) => !previous)}
            className="text-muted-foreground hover:text-foreground shrink-0 p-0.5"
          >
            {expanded ? (
              <ChevronDown aria-hidden="true" className="size-3.5" />
            ) : (
              <ChevronRight aria-hidden="true" className="size-3.5" />
            )}
          </button>
          <span className="text-foreground truncate text-xs font-semibold">{stip.title}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {stip.policyTag && isSafePolicyUrl(stip.policyTag) && (
            <a
              href={stip.policyTag}
              target="_blank"
              rel="noreferrer"
              aria-label={t("stipulations.policyAriaLabel")}
              className="text-foreground hover:bg-accent rounded-full p-1"
            >
              <Info aria-hidden="true" className="size-4" />
            </a>
          )}
          {stip.currentAction && (
            <StatusBadge
              status="open"
              label={stip.currentAction}
              className={
                stip.statusDisplay === "open"
                  ? "border-success-strong text-success-strong"
                  : "border-border text-muted-foreground"
              }
            />
          )}
        </div>
      </div>

      {stip.availableActions.length > 0 || stip.openLoanStip || stip.secondaryProofsAllowed ? (
        <div className="mt-1 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            {stip.availableActions.length > 0 && (
              <StipulationActionMenu
                actions={stip.availableActions}
                currentLabel={currentLabel}
                disabled={applyAction.isPending}
                onSelect={handleSelect}
              />
            )}
            {stip.openLoanStip && (
              <button
                type="button"
                onClick={() => void 0}
                className="bg-primary text-primary-foreground hover:bg-primary/90 ml-auto rounded px-1.5 py-0.5 text-xs"
              >
                {t("stipulations.openLoanCheck")}
              </button>
            )}
          </div>
          {stip.secondaryProofsAllowed && (
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={edit.porExceptionChecked ?? false}
                onChange={(event) => handlePorExceptionChange(event.target.checked)}
                disabled={porExceptionMutation.isPending}
                className="size-4"
              />
              <span>{t("stipulations.secondaryProofsLabel")}</span>
            </label>
          )}
        </div>
      ) : null}

      {selectedAction?.requiresNote && (
        <div className="mt-1">
          <Textarea
            rows={2}
            value={edit.note ?? ""}
            onChange={(event) => setNote(stip.id, event.target.value)}
            onBlur={(event) => {
              persistAction(selectedAction.actionCode, selectedReason?.reasonPk, event.target.value);
            }}
            placeholder={t("stipulations.dealerInstructionPlaceholder")}
            className="text-xs"
          />
        </div>
      )}

      {saveError && (
        <p role="alert" className="text-destructive mt-2 text-xs">
          {saveError}
        </p>
      )}

      {porError && (
        <p role="alert" className="text-destructive mt-2 text-xs">
          {porError}
        </p>
      )}

      {stip.recentNote && (stip.recentNote.actionName || stip.recentNote.auditInformation) && (
        <div className="text-muted-foreground mt-1 text-xs leading-tight">
          <div className="flex gap-1">
            {stip.recentNote.actionName && (
              <span className="text-primary font-semibold">{stip.recentNote.actionName}</span>
            )}
            {stip.recentNote.auditInformation && <span>{stip.recentNote.auditInformation}</span>}
          </div>
        </div>
      )}

      {expanded && (
        <fieldset className="border-border mt-1.5 rounded border p-1.5">
          <legend className="text-foreground px-1 text-xs font-semibold">
            {t("stipulations.instructionsHistory")}
          </legend>
          {isHistoryLoading && actionHistory.length === 0 ? (
            <div className="text-muted-foreground flex items-center gap-1 text-xs">
              <Loader2 aria-hidden="true" className="size-3 animate-spin" />
              {t("stipulations.historyLoading")}
            </div>
          ) : (
            actionHistory &&
            actionHistory.length > 0 && (
              <ul className="text-muted-foreground mb-1 flex flex-col gap-0.5 text-xs">
                {actionHistory.map((entry, index) => (
                  <li key={`${entry.actionCode ?? "action"}-${index}`} className="flex gap-1">
                    {entry.actionCode && (
                      <span className="text-primary font-bold">{snakeToTitleCase(entry.actionCode)}</span>
                    )}
                    {[entry.performedAt ? formatDatetime(entry.performedAt) : undefined, entry.performedBy]
                      .filter(Boolean)
                      .join(" · ")}
                  </li>
                ))}
              </ul>
            )
          )}
          <p className="text-muted-foreground text-xs">{stip.description ?? t("stipulations.noDescription")}</p>
        </fieldset>
      )}
    </div>
  );
}

export default StipulationRow;
